import {
  WORKSPACE_VIEW_ESTIMATED_BYTES_PER_TOKEN,
  WORKSPACE_VIEW_MAX_ESTIMATED_TOKENS,
  WORKSPACE_VIEW_MAX_INTERACTIONS,
  WORKSPACE_VIEW_MAX_NAVIGATION_VISITS,
  WORKSPACE_VIEW_MAX_NEIGHBOURS,
  WORKSPACE_VIEW_MAX_REF_VISITS,
  type WorkspaceViewSnapshot,
} from '@streamlain/copilot-realtime-contract';

const TEXT_UNITS =
  WORKSPACE_VIEW_MAX_ESTIMATED_TOKENS *
  WORKSPACE_VIEW_ESTIMATED_BYTES_PER_TOKEN;
// These are work quotas before JSON, not the final wire/token budget.
const RAW_UNITS = TEXT_UNITS * 4;
const VALUE_VISITS = 4096;
const MAX_DEPTH = 16;
type Context =
  | 'snapshot'
  | 'element'
  | 'interaction'
  | 'ref'
  | 'action'
  | 'other';
/** What an element leaves out when it is false or empty: it then says nothing. */
const SILENT_WHEN_EMPTY = new Set(['selected', 'disabled', 'text', 'refs']);
const isEmpty = (value: unknown) =>
  value === false || value === '' || (Array.isArray(value) && !value.length);
class AdmissionIssue extends Error {
  constructor(readonly reason: 'oversized' | 'invalid') {
    super(reason);
  }
}

/** Plain owner data only: no accessor execution, toJSON or unbounded deep clone. */
export function admitWorkspaceViewSnapshot(
  input: WorkspaceViewSnapshot
):
  | { ok: true; value: WorkspaceViewSnapshot }
  | { ok: false; reason: 'oversized' | 'invalid' } {
  let visits = 0;
  let units = 0;
  let truncated = false;
  const active = new WeakSet<object>();
  const copies = new WeakMap<object, { value: object; units: number }>();
  const issue = (reason: 'oversized' | 'invalid'): never => {
    throw new AdmissionIssue(reason);
  };
  const read = (object: object, key: string): unknown => {
    const descriptor = Object.getOwnPropertyDescriptor(object, key);
    if (!descriptor || !('value' in descriptor)) return issue('invalid');
    return descriptor.value;
  };
  const charge = (amount: number) => {
    if (units + amount > RAW_UNITS) issue('oversized');
    units += amount;
  };
  const array = (
    source: unknown[],
    context: Context,
    key: string,
    depth: number
  ): unknown[] => {
    let start = 0;
    let limit = WORKSPACE_VIEW_MAX_NAVIGATION_VISITS;
    let child: Context = 'other';
    let optional = false;
    // Offered destinations are not visible content: dropping some leaves the view whole.
    const offer = context === 'snapshot' && key === 'actions';
    if (key === 'refs' && (context === 'snapshot' || context === 'element')) {
      limit = WORKSPACE_VIEW_MAX_REF_VISITS;
      child = 'ref';
      optional = true;
    } else if (context === 'snapshot' && key === 'elements') {
      child = 'element';
      optional = true;
    } else if (context === 'snapshot' && key === 'actions') {
      child = 'action';
      optional = true;
    } else if (context === 'snapshot' && key === 'interactions') {
      limit = WORKSPACE_VIEW_MAX_INTERACTIONS;
      start = Math.max(0, source.length - limit);
      child = 'interaction';
      optional = true;
    } else if (context === 'interaction' && key === 'neighbours') {
      limit = WORKSPACE_VIEW_MAX_NEIGHBOURS;
      child = 'element';
      optional = true;
    }
    if (source.length > limit) {
      if (!optional) issue('oversized');
      truncated ||= !offer;
    }
    const end = Math.min(source.length, start + limit);
    // Reserve genuine search descriptors before optional metadata. Output order
    // stays the owner's order; this admission grants no activation authority.
    const indices: number[] = [];
    if (child === 'action') {
      for (let index = start; index < end; index++) {
        const entry = read(source, String(index));
        if (
          entry &&
          typeof entry === 'object' &&
          Object.getOwnPropertyDescriptor(entry, 'capability')?.value ===
            'workspace-search'
        )
          indices.push(index);
      }
    }
    const priority = new Set(indices);
    if (
      optional &&
      (units + 2 + end - start > RAW_UNITS || visits >= VALUE_VISITS)
    ) {
      if (
        (context === 'snapshot' && key === 'refs' && source.length) ||
        priority.size
      )
        issue('oversized');
      truncated ||= !offer;
      return [];
    }
    charge(2 + end - start);
    for (let index = start; index < end; index++)
      if (!priority.has(index)) indices.push(index);
    const admitted = new Map<number, unknown>();
    const arrayStart = units;
    for (const index of indices) {
      if (
        context === 'snapshot' &&
        (key === 'refs' || key === 'actions') &&
        !priority.has(index) &&
        index !== 0 &&
        units - arrayStart >= TEXT_UNITS
      ) {
        truncated ||= !offer;
        break;
      }
      const entry = read(source, String(index));
      const mandatory =
        (context === 'snapshot' && key === 'refs' && index === 0) ||
        priority.has(index);
      try {
        admitted.set(index, clone(entry, child, depth + 1));
      } catch (error) {
        if (
          !(error instanceof AdmissionIssue) ||
          error.reason === 'invalid' ||
          !optional ||
          mandatory
        )
          throw error;
        truncated ||= !offer;
        if (visits >= VALUE_VISITS || units >= RAW_UNITS) break;
      }
    }
    return [...admitted].sort(([a], [b]) => a - b).map(([, entry]) => entry);
  };
  const clone = (entry: unknown, context: Context, depth: number): unknown => {
    if (++visits > VALUE_VISITS || depth > MAX_DEPTH) return issue('oversized');
    if (typeof entry === 'string') {
      if (entry.length > TEXT_UNITS) return issue('oversized');
      charge(entry.length);
      return entry;
    }
    if (entry === null || entry === undefined || typeof entry === 'boolean') {
      charge(5);
      return entry;
    }
    if (typeof entry === 'number') {
      charge(24);
      return Number.isFinite(entry) ? entry : issue('invalid');
    }
    if (typeof entry !== 'object' || active.has(entry)) return issue('invalid');
    const cached = copies.get(entry);
    if (cached) {
      charge(cached.units);
      return cached.value;
    }
    if (Array.isArray(entry)) {
      if (entry.length > WORKSPACE_VIEW_MAX_NAVIGATION_VISITS)
        return issue('oversized');
      active.add(entry);
      try {
        charge(2 + entry.length);
        const items: unknown[] = [];
        for (let index = 0; index < entry.length; index++)
          items.push(clone(read(entry, String(index)), 'other', depth + 1));
        return items;
      } finally {
        active.delete(entry);
      }
    }
    const prototype: unknown = Object.getPrototypeOf(entry);
    if (prototype !== Object.prototype && prototype !== null)
      return issue('invalid');
    active.add(entry);
    const before = units;
    charge(2);
    const result: Record<string, unknown> = Object.create(null);
    try {
      // Bindings and capabilities are admitted before free excerpts/DOM metadata.
      const ordered: string[] = [];
      for (const key in entry) {
        if (!Object.hasOwn(entry, key)) continue;
        if (++visits > VALUE_VISITS) return issue('oversized');
        charge(key.length + 3);
        ordered.push(key);
      }
      ordered.sort((a, b) => {
        const priority = (key: string) =>
          context === 'snapshot'
            ? key === 'refs'
              ? 1
              : key === 'actions'
                ? 2
                : key === 'interactions'
                  ? 3
                  : key === 'summary'
                    ? 4
                    : key === 'elements'
                      ? 5
                      : 0
            : context === 'element' && (key === 'text' || key === 'label')
              ? 1
              : 0;
        return priority(a) - priority(b) || a.localeCompare(b);
      });
      for (const key of ordered) {
        const value = read(entry, key);
        let carried: unknown;
        if (
          typeof value === 'string' &&
          ((context === 'snapshot' && key === 'summary') ||
            (context === 'element' && (key === 'label' || key === 'text')))
        ) {
          let end = Math.min(value.length, TEXT_UNITS, RAW_UNITS - units);
          if (end < value.length) {
            truncated = true;
            const last = value.charCodeAt(end - 1);
            if (last >= 0xd800 && last <= 0xdbff) end--;
          }
          charge(end);
          carried = value.slice(0, end);
        } else if (Array.isArray(value)) {
          carried = array(value, context, key, depth);
        } else {
          carried = clone(
            value,
            context === 'interaction' && key === 'target' ? 'element' : 'other',
            depth + 1
          );
        }
        // An element is not selected, not disabled, without text and references unless it says so.
        if (
          context === 'element' &&
          SILENT_WHEN_EMPTY.has(key) &&
          isEmpty(carried)
        )
          continue;
        result[key] = carried;
      }
      copies.set(entry, { value: result, units: units - before });
      return result;
    } finally {
      active.delete(entry);
    }
  };
  try {
    const value = clone(input, 'snapshot', 0) as WorkspaceViewSnapshot;
    if (truncated) value.truncated = true;
    return { ok: true, value };
  } catch (error) {
    return {
      ok: false,
      reason: error instanceof AdmissionIssue ? error.reason : 'invalid',
    };
  }
}
